import {
  Button,
  JoonUIColor,
  Modal,
  ModalHeader,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import useQuestBoardMultiSelectStore from "./useQuestBoardMultiSelectStore"
import { useViewQuestModalStore } from "./viewQuestModal/useViewQuestModalStore"
import {
  useSetQuestActiveMutation,
  useSetQuestInactiveMutation,
} from "../../networking/quests/mutations"

export enum ToggleQuestActiveModalType {
  INACTIVE = "INACTIVE",
  ACTIVE = "ACTIVE",
}

interface ToggleQuestActiveModalStore {
  isOpen: boolean
  onOpen: (questSeriesIds: number[], type: ToggleQuestActiveModalType) => void
  onClose: () => void

  questSeriesIds: number[]
  modalType: ToggleQuestActiveModalType
}

export const useToggleQuestActiveModalStore =
  create<ToggleQuestActiveModalStore>((set) => ({
    isOpen: false,
    onOpen: (questSeriesIds: number[], modalType: ToggleQuestActiveModalType) =>
      set({ isOpen: true, questSeriesIds, modalType }),
    onClose: () => set({ isOpen: false }),
    questSeriesIds: [],
    modalType: ToggleQuestActiveModalType.INACTIVE,
  }))

const ToggleQuestActiveModal = () => {
  const { isOpen, onClose, questSeriesIds, modalType } =
    useToggleQuestActiveModalStore()

  const setQuestInactiveMutation = useSetQuestInactiveMutation()
  const setQuestActiveMutation = useSetQuestActiveMutation()
  const { onClose: closeViewQuestModal } = useViewQuestModalStore()
  const { setMultiSelectMode, clearSelectedQuestIds } =
    useQuestBoardMultiSelectStore()
  const { t } = useTranslation()
  const isPlural = questSeriesIds.length > 1

  const handleSetQuestInactive = async () => {
    await setQuestInactiveMutation.mutateAsync(questSeriesIds)
    onClose()
    closeViewQuestModal()

    setMultiSelectMode(false)
    clearSelectedQuestIds()
  }

  const handleSetQuestActive = async () => {
    await setQuestActiveMutation.mutateAsync(questSeriesIds)
    onClose()
    closeViewQuestModal()
    toast.success(
      isPlural ? t("QB_TOAST_SET_ACTIVE_OTHER") : t("QB_TOAST_SET_ACTIVE_ONE")
    )

    setMultiSelectMode(false)
    clearSelectedQuestIds()
  }

  if (modalType === ToggleQuestActiveModalType.ACTIVE) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} style={{ maxWidth: "95vw" }}>
        <ModalHeader
          onClose={onClose}
          title={
            isPlural
              ? t("QB_SET_ACTIVE_TITLE_OTHER")
              : t("QB_SET_ACTIVE_TITLE_ONE")
          }
          showBorderBottom
        />
        <div className="flex flex-col gap-4 p-6 pt-3">
          <Typography variant="body">{t("QB_SET_ACTIVE_DESC")}</Typography>
          <div className="flex justify-end items-center gap-2">
            <TextButton onClick={onClose} className="px-4 py-1">
              <Typography variant="body" color={JoonUIColor.semantic.primary}>
                {t("QB_CANCEL")}
              </Typography>
            </TextButton>
            <Button
              text={t("QB_SET_ACTIVE_BTN")}
              onClick={handleSetQuestActive}
              className="px-4 py-1"
            />
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      style={{ width: "min(450px, 95vw)" }}
    >
      <ModalHeader
        onClose={onClose}
        title={
          isPlural
            ? t("QB_SET_INACTIVE_TITLE_OTHER")
            : t("QB_SET_INACTIVE_TITLE_ONE")
        }
        showBorderBottom
      />
      <div className="flex flex-col gap-4 p-6 pt-3">
        <Typography variant="body">{t("QB_SET_INACTIVE_DESC")}</Typography>
        <img src="/images/inactive-quest-modal.png" alt="quest-inactive" />
        <div className="flex justify-end items-center gap-2">
          <TextButton onClick={onClose} className="px-4 py-1">
            <Typography variant="body" color={JoonUIColor.semantic.primary}>
              {t("QB_CANCEL")}
            </Typography>
          </TextButton>
          <Button
            text={t("QB_SET_INACTIVE")}
            onClick={handleSetQuestInactive}
            className="px-4 py-1"
            isLoading={setQuestInactiveMutation.isLoading}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ToggleQuestActiveModal
